import { useEffect, useRef, useCallback } from 'react';
import Shepherd from 'shepherd.js';
import { offset } from '@floating-ui/dom';
import { useTourStore, type TourId } from '../stores/tourStore';
import { useGlobalStore } from '../stores/globalStore';
import { getTourById } from './tourDefinitions';

// Import Shepherd's base CSS + our custom overrides
import 'shepherd.js/dist/css/shepherd.css';
import './tour.css';

/**
 * Hook that manages a Shepherd.js tour instance.
 *
 * Usage:
 *   const { startTour } = useTour();
 *   startTour('dashboard');
 *
 * The hook reacts to `activeTourId` in the tour store. When a tour ID is set,
 * the corresponding Shepherd tour is created and started. When the tour ends
 * (complete or cancel), the store is updated.
 */
export function useTour() {
  const tourRef = useRef<InstanceType<typeof Shepherd.Tour> | null>(null);
  const activeTourId = useTourStore((s) => s.activeTourId);
  const completeTour = useTourStore((s) => s.completeTour);
  const cancelTour = useTourStore((s) => s.cancelTour);
  const userId = useGlobalStore((s) => s.userId);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (tourRef.current) {
        tourRef.current.cancel();
        tourRef.current = null;
      }
    };
  }, []);

  // React to activeTourId changes
  useEffect(() => {
    // Tear down previous tour if any
    if (tourRef.current) {
      tourRef.current.cancel();
      tourRef.current = null;
    }

    if (!activeTourId) return;

    const definition = getTourById(activeTourId);
    if (!definition) {
      cancelTour();
      return;
    }

    // Create new Shepherd tour
    const tour = new Shepherd.Tour({
      useModalOverlay: true,
      defaultStepOptions: {
        scrollTo: { behavior: 'smooth', block: 'center' },
        cancelIcon: { enabled: true },
        modalOverlayOpeningPadding: 8,
        modalOverlayOpeningRadius: 8,
        floatingUIOptions: {
          middleware: [offset(16)],
        },
      },
    });

    // Add steps with progress tracking
    const totalSteps = definition.steps.length;
    definition.steps.forEach((step, index) => {
      tour.addStep({
        ...step,
        // Inject progress bar into each step
        when: {
          ...step.when,
          show() {
            // Add progress bar
            const el = tour.currentStep?.getElement();
            if (el) {
              // Remove existing progress bar
              const existing = el.querySelector('.shepherd-progress');
              if (existing) existing.remove();

              const progressBar = document.createElement('div');
              progressBar.className = 'shepherd-progress';
              progressBar.style.width = `${((index + 1) / totalSteps) * 100}%`;
              el.prepend(progressBar);

              // Add step counter to header
              const header = el.querySelector('.shepherd-header');
              if (header && !header.querySelector('.shepherd-step-counter')) {
                const counter = document.createElement('span');
                counter.className = 'shepherd-step-counter';
                counter.textContent = `${index + 1} of ${totalSteps}`;
                header.appendChild(counter);
              }

              // Add pulse effect to target
              const target = step.attachTo?.element;
              if (target && typeof target === 'string') {
                const targetEl = document.querySelector(target);
                if (targetEl) {
                  targetEl.classList.add('shepherd-target-pulse');
                }
              }
            }
          },
          hide() {
            // Remove pulse effect
            const target = step.attachTo?.element;
            if (target && typeof target === 'string') {
              const targetEl = document.querySelector(target);
              if (targetEl) {
                targetEl.classList.remove('shepherd-target-pulse');
              }
            }
          },
        },
      });
    });

    // Event handlers
    tour.on('complete', () => {
      completeTour(activeTourId, userId);
      tourRef.current = null;
    });

    tour.on('cancel', () => {
      cancelTour();
      tourRef.current = null;
      // Clean up any remaining pulse classes
      document.querySelectorAll('.shepherd-target-pulse').forEach((el) => {
        el.classList.remove('shepherd-target-pulse');
      });
    });

    tourRef.current = tour;

    // Small delay to let the page render before attaching
    const timer = setTimeout(() => {
      tour.start();
    }, 300);

    return () => clearTimeout(timer);
  }, [activeTourId, cancelTour, completeTour, userId]);

  const start = useCallback(
    (id: TourId) => {
      useTourStore.getState().startTour(id);
    },
    [],
  );

  return { startTour: start };
}
